import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { FeedbackAnswerPayload, FeedbackTemplateQuestion } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { parseAocIdsFromQuery } from "src/app/util/aoc-filter-options.utils";
import { feedbackQuestionNeedsAnswer, feedbackQuestionNeedsRating } from "../feedback-question.utils";
import { FeedbackResponseDetail } from "../feedback.types";

@Component({
	selector: "app-feedback-response-detail",
	templateUrl: "./feedback-response-detail.component.html",
	styleUrls: ["./feedback-response-detail.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FeedbackResponseDetailComponent implements OnInit, OnDestroy {
	readonly routeType = RouteType;
	templateId = "";
	submissionId = "";
	selectedAocIds: string[] = [];
	response: FeedbackResponseDetail | null = null;
	pageIndex = 0;
	readonly pageSize = 10;

	private subs = new Subscription();

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private roleService: RoleService,
		private sharedService: SharedService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FEEDBACK);
		this.subs.add(
			this.route.queryParamMap.subscribe((params) => {
				const aocIds = params.getAll("aocIds");
				this.selectedAocIds = aocIds.length ? aocIds : parseAocIdsFromQuery(params.get("aocId"));
			}),
		);
		this.subs.add(
			this.route.paramMap.subscribe((params) => {
				this.templateId = params.get("id") ?? "";
				this.submissionId = params.get("submissionId") ?? "";
				if (this.templateId && this.submissionId) {
					this.loadResponse();
				}
			}),
		);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	get questions(): FeedbackTemplateQuestion[] {
		return this.response?.template?.questions ?? [];
	}

	needsRating(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsRating(question);
	}

	needsAnswer(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsAnswer(question);
	}

	get pagedQuestions(): FeedbackTemplateQuestion[] {
		const start = this.pageIndex * this.pageSize;
		return this.questions.slice(start, start + this.pageSize);
	}

	get totalPages(): number {
		return Math.max(1, Math.ceil(this.questions.length / this.pageSize));
	}

	get pageNumbers(): number[] {
		return Array.from({ length: this.totalPages }, (_, index) => index);
	}

	get globalQuestionOffset(): number {
		return this.pageIndex * this.pageSize;
	}

	loadResponse(): void {
		this.loaderService.setLoading(true);
		this.subs.add(
			this.httpService.getFeedbackResponse(this.templateId, this.submissionId, this.buildScopeParams()).subscribe({
				next: (resp) => {
					this.response = (resp?.data ?? resp) as FeedbackResponseDetail;
					this.loaderService.setLoading(false);
					this.cdr.detectChanges();
				},
				error: () => this.loaderService.setLoading(false),
			}),
		);
	}

	answerFor(questionId: string): FeedbackAnswerPayload | undefined {
		return (this.response?.answers ?? []).find((a) => a.questionId === questionId);
	}

	questionNumber(index: number): number {
		return this.globalQuestionOffset + index + 1;
	}

	prevPage(): void {
		if (this.pageIndex > 0) this.pageIndex -= 1;
	}

	nextPage(): void {
		if (this.pageIndex < this.totalPages - 1) this.pageIndex += 1;
	}

	back(): void {
		const queryParams: { aocIds?: string[] } = {};
		if (this.roleService.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		this.router.navigate([RouteType.FEEDBACK, this.templateId, "responses"], {
			queryParams,
		});
	}

	private buildScopeParams(): { role: string; aocIds?: string[] } {
		const params: { role: string; aocIds?: string[] } = {
			role: this.roleService.getUserRole(),
		};
		if (this.roleService.isGroupAdmin && this.selectedAocIds.length) {
			params.aocIds = this.selectedAocIds;
		}
		return params;
	}
}
